import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input, Modal, Select, Spin } from "antd";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  AlertChannels,
  AlertDirection,
  AlertRecipient,
  AlertScopeOptions,
  AlertScopeType,
  AlertType,
  ApiAlertRule,
  createAlert,
  getAlertScopeOptions,
  previewAlertRecipients,
  searchAlertRecipients,
  updateAlert,
} from "./balancedScorecard.service";
import "./AlertEditorModal.css";

interface AlertEditorModalProps {
  open: boolean;
  scorecardId: string;
  /** null = create a new alert (starts at the type step). */
  rule: ApiAlertRule | null;
  onClose: () => void;
  onSaved: () => void;
}

const errorDetail = (err: any) => err?.response?.data?.detail;

const AlertEditorModal: React.FC<AlertEditorModalProps> = ({
  open,
  scorecardId,
  rule,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation("balancedScorecard");
  const isEdit = rule !== null;
  const [step, setStep] = useState<"type" | "form">("type");
  const [type, setType] = useState<AlertType>("threshold");
  const [name, setName] = useState("");
  const [scopeType, setScopeType] = useState<AlertScopeType>("all");
  const [domainIds, setDomainIds] = useState<string[]>([]);
  const [indicatorIds, setIndicatorIds] = useState<string[]>([]);
  const [direction, setDirection] = useState<AlertDirection>("decreases");
  const [day, setDay] = useState(0);
  const [time, setTime] = useState("08:00");
  const [channels, setChannels] = useState<AlertChannels>({
    in_app: true,
    email: true,
  });
  // Keeps the labels of every selected person, even after a new search.
  const [recipients, setRecipients] = useState<AlertRecipient[]>([]);
  const [recipientOptions, setRecipientOptions] = useState<AlertRecipient[]>(
    [],
  );
  const [recipientQuery, setRecipientQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [scopeOptions, setScopeOptions] = useState<AlertScopeOptions | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const searchSeq = useRef(0);

  const typeOptions = [
    {
      value: "threshold" as AlertType,
      title: t("alerts.thresholdTitle"),
      description: t("alerts.thresholdDesc"),
    },
    {
      value: "weekly_digest" as AlertType,
      title: t("alerts.digestTitle"),
      description: t("alerts.digestDesc"),
    },
  ];

  const dayOptions = [0, 1, 2, 3, 4, 5, 6].map((i) => ({
    label: t(`alerts.days.${i}`),
    value: i,
  }));

  // Reset when opened. Edit starts at the form; create starts at the type step.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setRecipientQuery("");
    setRecipientOptions([]);
    if (rule) {
      setStep("form");
      setType(rule.alert_type);
      setName(rule.name);
      setScopeType(rule.scope_type);
      // The list returns objects; the PATCH body needs plain id strings.
      setDomainIds(rule.domains.map((d) => d.id));
      setIndicatorIds(rule.indicators.map((i) => i.id));
      setDirection(rule.direction ?? "decreases");
      setDay(rule.schedule?.day ?? 0);
      setTime(rule.schedule?.time?.slice(0, 5) ?? "08:00");
      setChannels(rule.channels);
      setRecipients(rule.recipients);
    } else {
      setStep("type");
      setType("threshold");
      setName("");
      setScopeType("all");
      setDomainIds([]);
      setIndicatorIds([]);
      setDirection("decreases");
      setDay(0);
      setTime("08:00");
      setChannels({ in_app: true, email: true });
      setRecipients([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Scope options (domains and indicators) are only needed by threshold rules.
  useEffect(() => {
    if (!open || type !== "threshold" || scopeOptions) return;
    getAlertScopeOptions(scorecardId)
      .then(setScopeOptions)
      .catch(() => toast.error(t("alerts.loadScopeFailed")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, scorecardId, scopeOptions]);

  // Live "notify N people" line. Uses the server's permission rules.
  const recipientIdsKey = recipients.map((r) => r.user_id).join(",");
  useEffect(() => {
    if (!open || step !== "form") return;
    let cancelled = false;
    previewAlertRecipients(
      scorecardId,
      recipients.map((r) => r.user_id),
    )
      .then((res) => !cancelled && setPreviewCount(res.count))
      .catch(() => !cancelled && setPreviewCount(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, scorecardId, recipientIdsKey]);

  const handleRecipientSearch = (q: string) => {
    setRecipientQuery(q);
    if (!q.trim()) {
      setRecipientOptions([]);
      return;
    }
    const seq = ++searchSeq.current;
    setSearching(true);
    searchAlertRecipients(scorecardId, q.trim())
      .then((results) => {
        if (seq === searchSeq.current) setRecipientOptions(results);
      })
      .catch(() => {
        if (seq === searchSeq.current) setRecipientOptions([]);
      })
      .finally(() => {
        if (seq === searchSeq.current) setSearching(false);
      });
  };

  const recipientSelectOptions = useMemo(() => {
    const byId = new Map<number, AlertRecipient>();
    [...recipients, ...recipientOptions].forEach((r) => byId.set(r.user_id, r));
    return Array.from(byId.values()).map((r) => ({
      value: r.user_id,
      label: r.has_access
        ? `${r.name} (${r.email})`
        : `${r.name} (${r.email}) · ${t("alerts.noAccess")}`,
      disabled: !r.has_access,
    }));
  }, [recipients, recipientOptions, t]);

  const handleRecipientChange = (ids: number[]) => {
    const byId = new Map<number, AlertRecipient>();
    [...recipients, ...recipientOptions].forEach((r) => byId.set(r.user_id, r));
    setRecipients(
      ids
        .map((id) => byId.get(id))
        .filter((r): r is AlertRecipient => Boolean(r)),
    );
  };

  const groupedIndicatorOptions = useMemo(() => {
    if (!scopeOptions) return [];
    const groups = new Map<string, { label: string; value: string }[]>();
    scopeOptions.indicators.forEach((i) => {
      const list = groups.get(i.domain_name) ?? [];
      list.push({ label: i.name, value: i.id });
      groups.set(i.domain_name, list);
    });
    return Array.from(groups.entries()).map(([label, options]) => ({
      label,
      options,
    }));
  }, [scopeOptions]);

  const validate = (): string | null => {
    if (type !== "weekly_digest" && !channels.in_app && !channels.email) {
      return t("alerts.channelRequired");
    }
    if (type === "threshold") {
      if (scopeType === "domains" && domainIds.length === 0) {
        return t("alerts.selectDomainsRequired");
      }
      if (scopeType === "indicators" && indicatorIds.length === 0) {
        return t("alerts.selectIndicatorsRequired");
      }
    }
    return null;
  };

  const buildPayload = () => {
    const base = {
      name: name.trim() || null,
      recipient_user_ids: recipients.map((r) => r.user_id),
    };
    if (type === "weekly_digest") {
      return {
        ...base,
        schedule: { day, time: `${time}:00` },
      };
    }
    if (type === "publish_notification") {
      return { ...base, channels };
    }
    return {
      ...base,
      scope_type: scopeType,
      domain_ids: scopeType === "domains" ? domainIds : [],
      indicator_ids: scopeType === "indicators" ? indicatorIds : [],
      direction,
      channels,
    };
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const noAccess = recipients.filter((r) => !r.has_access);
    if (noAccess.length > 0) {
      setError(
        t("alerts.noAccessNames", {
          names: noAccess.map((r) => r.name).join(", "),
        }),
      );
      return;
    }
    setError(null);
    setSaving(true);
    try {
      if (rule) {
        await updateAlert(scorecardId, rule.id, buildPayload());
        toast.success(t("alerts.updated"));
      } else {
        await createAlert(scorecardId, {
          alert_type: type,
          ...buildPayload(),
          is_enabled: true,
        });
        toast.success(t("alerts.created"));
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const detail = errorDetail(err);
      if (detail?.code === "bsc_alert_recipient_no_access") {
        setError(t("alerts.noAccessGeneric"));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(
          rule ? t("alerts.updateFailed") : t("alerts.createFailed"),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const showTypeStep = !isEdit && step === "type";
  const previewText =
    previewCount === null
      ? null
      : t("alerts.notifyCount", { count: previewCount });

  return (
    <Modal
      open={open}
      title={isEdit ? t("alerts.editTitle") : t("alerts.addTitle")}
      onCancel={onClose}
      width={460}
      destroyOnClose
      className="bsc-alert-modal"
      footer={null}
    >
      {showTypeStep ? (
        <>
          <div className="bsc-alert-label">{t("alerts.chooseTitle")}</div>
          <div className="bsc-alert-type-list">
            {typeOptions.map((opt) => (
              <label
                key={opt.value}
                className={`bsc-alert-type-option ${type === opt.value ? "is-selected" : ""}`}
              >
                <input
                  type="radio"
                  name="alert-type"
                  checked={type === opt.value}
                  onChange={() => setType(opt.value)}
                />
                <span>
                  <span className="bsc-alert-type-title">{opt.title}</span>
                  <span className="bsc-alert-type-desc">{opt.description}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="bsc-alert-footer">
            <button type="button" className="bsc-btn-cancel" onClick={onClose}>
              {t("alerts.cancel")}
            </button>
            <button
              type="button"
              className="bsc-dim-save"
              onClick={() => setStep("form")}
            >
              {t("alerts.continue")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="bsc-alert-typeline">
            <span>
              {t("alerts.typeLabel", {
                type: t(`alerts.types.${type}`),
              })}
            </span>
            {!isEdit && (
              <button
                type="button"
                className="bsc-alert-link"
                onClick={() => setStep("type")}
              >
                {t("alerts.change")}
              </button>
            )}
            {rule?.is_default && (
              <span className="bsc-alert-default">{t("alerts.default")}</span>
            )}
          </div>

          <label className="bsc-alert-label">{t("alerts.name")}</label>
          <Input
            value={name}
            placeholder={t("alerts.namePlaceholder")}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="bsc-alert-hint">{t("alerts.nameHint")}</div>

          {type === "threshold" && (
            <>
              <label className="bsc-alert-label">
                {t("alerts.notifyWhen")}
              </label>
              <div className="bsc-alert-radio-col">
                {(
                  [
                    ["all", t("alerts.scopeAll")],
                    ["domains", t("alerts.scopeDomains")],
                    ["indicators", t("alerts.scopeIndicators")],
                  ] as [AlertScopeType, string][]
                ).map(([value, label]) => (
                  <label key={value} className="bsc-alert-radio">
                    <input
                      type="radio"
                      checked={scopeType === value}
                      onChange={() => setScopeType(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>

              {scopeType === "domains" && (
                <Select
                  mode="multiple"
                  className="bsc-alert-select"
                  placeholder={t("alerts.selectDomains")}
                  value={domainIds}
                  onChange={setDomainIds}
                  options={(scopeOptions?.domains ?? []).map((d) => ({
                    label: d.name,
                    value: d.id,
                  }))}
                  loading={!scopeOptions}
                />
              )}
              {scopeType === "indicators" && (
                <Select
                  mode="multiple"
                  className="bsc-alert-select"
                  placeholder={t("alerts.selectIndicators")}
                  value={indicatorIds}
                  onChange={setIndicatorIds}
                  options={groupedIndicatorOptions}
                  loading={!scopeOptions}
                  showSearch
                  optionFilterProp="label"
                />
              )}

              <label className="bsc-alert-label">
                {t("alerts.notifyWhen")}
              </label>
              <div className="bsc-alert-radio-col">
                {(
                  [
                    ["decreases", t("alerts.decreases")],
                    ["increases", t("alerts.increases")],
                  ] as [AlertDirection, string][]
                ).map(([value, label]) => (
                  <label key={value} className="bsc-alert-radio">
                    <input
                      type="radio"
                      checked={direction === value}
                      onChange={() => setDirection(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </>
          )}

          {type === "weekly_digest" && (
            <div className="bsc-alert-row">
              <div className="bsc-alert-col">
                <label className="bsc-alert-label">{t("alerts.day")}</label>
                <Select
                  value={day}
                  onChange={setDay}
                  options={dayOptions}
                  className="bsc-alert-select"
                />
              </div>
              <div className="bsc-alert-col">
                <label className="bsc-alert-label">{t("alerts.time")}</label>
                <Input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <label className="bsc-alert-label">{t("alerts.recipients")}</label>
          <Select
            mode="multiple"
            className="bsc-alert-select"
            placeholder={t("alerts.recipientsPlaceholder")}
            value={recipients.map((r) => r.user_id)}
            onChange={handleRecipientChange}
            onSearch={handleRecipientSearch}
            searchValue={recipientQuery}
            filterOption={false}
            notFoundContent={
              searching ? (
                <Spin size="small" />
              ) : recipientQuery.trim() ? (
                t("alerts.userNotFound")
              ) : (
                t("alerts.startTyping")
              )
            }
            options={recipientSelectOptions}
          />
          {previewText && <div className="bsc-alert-hint">{previewText}</div>}

          {type !== "weekly_digest" && (
            <>
              <label className="bsc-alert-label">{t("alerts.how")}</label>
              <div className="bsc-alert-checks">
                <label className="bsc-alert-radio">
                  <input
                    type="checkbox"
                    checked={channels.in_app}
                    onChange={(e) =>
                      setChannels((c) => ({ ...c, in_app: e.target.checked }))
                    }
                  />
                  {t("alerts.inApp")}
                </label>
                <label className="bsc-alert-radio">
                  <input
                    type="checkbox"
                    checked={channels.email}
                    onChange={(e) =>
                      setChannels((c) => ({ ...c, email: e.target.checked }))
                    }
                  />
                  {t("alerts.email")}
                </label>
              </div>
            </>
          )}

          {error && <div className="bsc-dim-error">{error}</div>}

          <div className="bsc-alert-footer">
            <button type="button" className="bsc-btn-cancel" onClick={onClose}>
              {t("alerts.cancel")}
            </button>
            <button
              type="button"
              className="bsc-dim-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving && <Spin size="small" />} {t("alerts.saveAlert")}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default AlertEditorModal;
